import React, { useEffect, useRef, useState } from "react";
import { useNavigate } from "react-router-dom";
import api from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { SELLER_TYPES, CATEGORIES, UNITS, CERT_TYPES } from "@/lib/constants";
import ImageUpload from "@/components/ImageUpload";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Card } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { Switch } from "@/components/ui/switch";
import { Separator } from "@/components/ui/separator";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import {
  Sparkles, Send, Loader2, ArrowLeft, ClipboardCheck, Leaf, MapPin,
  Store, Truck, AlertTriangle, CheckCircle2, ImagePlus, Camera,
} from "lucide-react";
import { toast } from "sonner";

// Render assistant text: keep line breaks, strip markdown bold markers.
const renderText = (t) => (t || "").replace(/\*\*(.*?)\*\*/g, "$1");

export default function OnboardingAssistant() {
  const navigate = useNavigate();
  const { user, setUser } = useAuth();

  const [sessionId, setSessionId] = useState(null);
  const [messages, setMessages] = useState([]);
  const [input, setInput] = useState("");
  const [sending, setSending] = useState(false);
  const [starting, setStarting] = useState(true);
  const [view, setView] = useState("chat"); // chat | review | done
  const [extracting, setExtracting] = useState(false);
  const [draft, setDraft] = useState(null);
  const [flags, setFlags] = useState([]);
  const [submitting, setSubmitting] = useState(false);
  const [account, setAccount] = useState({ full_name: "", email: "", password: "" });
  const [result, setResult] = useState({ published: false, message: "" });
  const scrollRef = useRef(null);

  useEffect(() => {
    (async () => {
      try {
        const res = await api.post("/onboarding/start");
        setSessionId(res.data.session_id);
        setMessages([{ role: "assistant", content: res.data.message }]);
      } catch (e) {
        toast.error("Couldn't start the assistant. Please try again.");
      } finally { setStarting(false); }
    })();
  }, []);

  useEffect(() => {
    if (scrollRef.current) scrollRef.current.scrollTop = scrollRef.current.scrollHeight;
  }, [messages, sending]);

  const send = async () => {
    const text = input.trim();
    if (!text || sending || !sessionId) return;
    setInput("");
    setMessages((m) => [...m, { role: "user", content: text }]);
    setSending(true);
    try {
      const res = await api.post(`/onboarding/${sessionId}/message`, { message: text });
      setMessages((m) => [...m, { role: "assistant", content: res.data.reply }]);
    } catch (e) {
      toast.error(e.response?.data?.detail || "The assistant didn't respond. Please try again.");
      setMessages((m) => [...m, { role: "assistant", content: "Sorry — I had a hiccup there. Could you say that again?" }]);
    } finally { setSending(false); }
  };

  const buildListing = async () => {
    if (!sessionId) return;
    setExtracting(true);
    try {
      const res = await api.post(`/onboarding/${sessionId}/extract`);
      setDraft(res.data.draft);
      setFlags(res.data.draft.flags || []);
      setView("review");
    } catch (e) {
      toast.error(e.response?.data?.detail || "I need a little more detail first. Tell me a bit more in the chat.");
    } finally { setExtracting(false); }
  };

  const setFarm = (k, v) => setDraft((d) => ({ ...d, farm: { ...d.farm, [k]: v } }));
  const setProduct = (i, k, v) => setDraft((d) => {
    const products = [...d.products];
    products[i] = { ...products[i], [k]: v };
    return { ...d, products };
  });

  const submit = async () => {
    if (!draft) return;
    setSubmitting(true);
    try {
      const payload = { draft };
      if (!user) payload.account = account;
      const res = await api.post(`/onboarding/${sessionId}/submit`, payload);
      if (res.data.token) {
        localStorage.setItem("fg_token", res.data.token);
        if (setUser) setUser(res.data.user);
      }
      setResult({ published: !!res.data.published, message: res.data.message || "" });
      setView("done");
    } catch (e) {
      toast.error(e.response?.data?.detail || "Couldn't submit your listing. Please check the details.");
    } finally { setSubmitting(false); }
  };

  // ---------- DONE ----------
  if (view === "done") {
    return (
      <div className="pt-16 min-h-screen bg-grain">
        <div className="max-w-xl mx-auto px-4 sm:px-6 py-16 text-center">
          <div className={`mx-auto h-16 w-16 rounded-2xl grid place-items-center mb-4 ${result.published ? "bg-[#E6F2E8]" : "bg-[#FBF3E2]"}`}>
            {result.published
              ? <CheckCircle2 className="h-8 w-8 text-[#1F5A2F]" />
              : <ClipboardCheck className="h-8 w-8 text-[#7A5A1F]" />}
          </div>
          <h1 className="text-2xl font-semibold" data-testid="onboarding-done-title">
            {result.published ? "Your listing is live!" : "Listing submitted for review"}
          </h1>
          <p className="text-muted-foreground mt-2" data-testid="onboarding-done-message">
            {result.message || (result.published
              ? "Your listing is now on the map for shoppers to find."
              : "Our team will give your listing a quick check and it'll appear on the map soon.")}
          </p>
          <div className="flex flex-col sm:flex-row gap-3 justify-center mt-6">
            <Button onClick={() => navigate("/sell/dashboard")} data-testid="onboarding-go-dashboard">Go to my dashboard</Button>
            {result.published && <Button variant="secondary" onClick={() => navigate("/map")}>View on the map</Button>}
          </div>
        </div>
      </div>
    );
  }

  // ---------- REVIEW ----------
  if (view === "review" && draft) {
    const f = draft.farm;
    const requiresSource = !["Farm", "Smallholding"].includes(f.seller_type);
    return (
      <div className="pt-16 min-h-screen bg-grain">
        <div className="max-w-3xl mx-auto px-4 sm:px-6 py-8">
          <button onClick={() => setView("chat")} className="flex items-center gap-1 text-sm text-muted-foreground hover:text-foreground mb-3" data-testid="review-back-to-chat">
            <ArrowLeft className="h-4 w-4" /> Back to chat to make changes
          </button>
          <div className="flex items-center gap-2 mb-1">
            <ClipboardCheck className="h-5 w-5 text-primary" />
            <h1 className="text-2xl font-semibold" data-testid="review-title">Here's your listing — does this look right?</h1>
          </div>
          <p className="text-muted-foreground text-sm mb-5">Add your photos, tweak anything, then publish. Most listings go live on the map straight away — a few (organic-certified, raw milk, or anything unclear) get a quick check by our team first.</p>

          {flags.length > 0 && (
            <Card className="rounded-xl p-4 mb-5 border-[#E8C97A] bg-[#FBF3E2]" data-testid="review-flags">
              <div className="flex items-center gap-2 font-medium text-[#7A5A1F] mb-1.5"><AlertTriangle className="h-4 w-4" /> A few things to check</div>
              <ul className="list-disc pl-5 text-sm text-[#7A5A1F] space-y-0.5">
                {flags.map((fl, i) => <li key={i}>{fl}</li>)}
              </ul>
            </Card>
          )}

          {/* Business */}
          <Card className="rounded-2xl p-6 mb-4 space-y-4">
            <h2 className="font-semibold flex items-center gap-2"><Store className="h-4 w-4 text-primary" /> Your business</h2>
            <div className="grid sm:grid-cols-2 gap-4">
              <div className="space-y-1.5">
                <Label>Business name</Label>
                <Input value={f.farm_name} onChange={(e) => setFarm("farm_name", e.target.value)} data-testid="review-farm-name" />
              </div>
              <div className="space-y-1.5">
                <Label>Producer type</Label>
                <Select value={f.seller_type} onValueChange={(v) => setFarm("seller_type", v)}>
                  <SelectTrigger data-testid="review-seller-type"><SelectValue /></SelectTrigger>
                  <SelectContent>{SELLER_TYPES.map((t) => <SelectItem key={t} value={t}>{t}</SelectItem>)}</SelectContent>
                </Select>
              </div>
            </div>
            <div className="space-y-1.5">
              <Label>Description</Label>
              <Textarea rows={2} value={f.description} onChange={(e) => setFarm("description", e.target.value)} data-testid="review-description" />
            </div>
            <div className="space-y-1.5">
              <Label className="flex items-center gap-1.5"><Camera className="h-4 w-4" /> Business photo</Label>
              <p className="text-xs text-muted-foreground -mt-1">Listings with photos get far more orders. You can add this later if you don't have one to hand.</p>
              <ImageUpload value={f.photo_url} onChange={(url) => setFarm("photo_url", url)} label="Add a photo of your business" testid="review-farm-photo" />
            </div>
          </Card>

          {/* Address */}
          <Card className="rounded-2xl p-6 mb-4 space-y-4">
            <h2 className="font-semibold flex items-center gap-2"><MapPin className="h-4 w-4 text-primary" /> Address &amp; map pin</h2>
            <div className="grid sm:grid-cols-2 gap-4">
              <div className="space-y-1.5"><Label>Address line 1</Label><Input value={f.address_line1} onChange={(e) => setFarm("address_line1", e.target.value)} data-testid="review-address1" /></div>
              <div className="space-y-1.5"><Label>Address line 2</Label><Input value={f.address_line2} onChange={(e) => setFarm("address_line2", e.target.value)} /></div>
              <div className="space-y-1.5"><Label>Town</Label><Input value={f.town} onChange={(e) => setFarm("town", e.target.value)} data-testid="review-town" /></div>
              <div className="space-y-1.5"><Label>County</Label><Input value={f.county} onChange={(e) => setFarm("county", e.target.value)} /></div>
              <div className="space-y-1.5"><Label>Postcode</Label><Input value={f.postcode} onChange={(e) => setFarm("postcode", e.target.value)} data-testid="review-postcode" /></div>
            </div>
          </Card>

          {/* Fulfilment */}
          <Card className="rounded-2xl p-6 mb-4 space-y-4">
            <h2 className="font-semibold flex items-center gap-2"><Truck className="h-4 w-4 text-primary" /> How customers get their order</h2>
            <div className="flex items-center justify-between rounded-lg border border-border p-3">
              <div className="flex items-center gap-2"><Store className="h-4 w-4" /> Collection available</div>
              <Switch checked={!!f.collection_available} onCheckedChange={(v) => setFarm("collection_available", v)} data-testid="review-collection-switch" />
            </div>
            {f.collection_available && (
              <div className="space-y-1.5"><Label>Collection details (days &amp; times)</Label><Input value={f.collection_info} onChange={(e) => setFarm("collection_info", e.target.value)} data-testid="review-collection-info" /></div>
            )}
            <div className="flex items-center justify-between rounded-lg border border-border p-3">
              <div className="flex items-center gap-2"><Truck className="h-4 w-4" /> Delivery available</div>
              <Switch checked={!!f.delivery_available} onCheckedChange={(v) => setFarm("delivery_available", v)} data-testid="review-delivery-switch" />
            </div>
            {f.delivery_available && (
              <div className="grid sm:grid-cols-2 gap-4">
                <div className="space-y-1.5"><Label>Delivery radius (miles)</Label><Input type="number" value={f.delivery_radius_miles} onChange={(e) => setFarm("delivery_radius_miles", Number(e.target.value))} /></div>
                <div className="space-y-1.5"><Label>Delivery fee (£)</Label><Input type="number" value={f.delivery_fee} onChange={(e) => setFarm("delivery_fee", Number(e.target.value))} /></div>
              </div>
            )}
          </Card>

          {/* Certification */}
          <Card className="rounded-2xl p-6 mb-4 space-y-4">
            <h2 className="font-semibold flex items-center gap-2"><Leaf className="h-4 w-4 text-primary" /> Certification</h2>
            <div className="grid sm:grid-cols-2 gap-4">
              <div className="space-y-1.5">
                <Label>Certification type</Label>
                <Select value={f.cert_type} onValueChange={(v) => setFarm("cert_type", v)}>
                  <SelectTrigger data-testid="review-cert-type"><SelectValue /></SelectTrigger>
                  <SelectContent>{CERT_TYPES.map((c) => <SelectItem key={c.value} value={c.value}>{c.label}</SelectItem>)}</SelectContent>
                </Select>
              </div>
              {(f.cert_type === "certified_organic" || f.cert_type === "other") && (
                <>
                  <div className="space-y-1.5"><Label>Certification body</Label><Input value={f.cert_body} onChange={(e) => setFarm("cert_body", e.target.value)} /></div>
                  <div className="space-y-1.5"><Label>Certificate number</Label><Input value={f.cert_number} onChange={(e) => setFarm("cert_number", e.target.value)} data-testid="review-cert-number" /></div>
                </>
              )}
            </div>
          </Card>

          {/* Products */}
          <h2 className="font-semibold flex items-center gap-2 mb-2 mt-6"><Leaf className="h-4 w-4 text-primary" /> Your products ({draft.products.length})</h2>
          <div className="space-y-4">
            {draft.products.map((p, i) => (
              <Card key={i} className="rounded-2xl p-5 space-y-4" data-testid="review-product-card">
                <div className="grid sm:grid-cols-[120px_1fr] gap-4">
                  <ImageUpload value={p.image_url} onChange={(url) => setProduct(i, "image_url", url)} label="Photo" aspect="aspect-square" testid={`review-product-photo-${i}`} />
                  <div className="space-y-3">
                    <div className="grid sm:grid-cols-2 gap-3">
                      <div className="space-y-1.5"><Label>Name</Label><Input value={p.name} onChange={(e) => setProduct(i, "name", e.target.value)} data-testid={`review-product-name-${i}`} /></div>
                      <div className="space-y-1.5">
                        <Label>Category</Label>
                        <Select value={p.category} onValueChange={(v) => setProduct(i, "category", v)}>
                          <SelectTrigger><SelectValue /></SelectTrigger>
                          <SelectContent>{CATEGORIES.map((c) => <SelectItem key={c} value={c}>{c}</SelectItem>)}</SelectContent>
                        </Select>
                      </div>
                      <div className="space-y-1.5"><Label>Price (£)</Label><Input type="number" value={p.price} onChange={(e) => setProduct(i, "price", Number(e.target.value))} data-testid={`review-product-price-${i}`} /></div>
                      <div className="space-y-1.5">
                        <Label>Unit</Label>
                        <Select value={p.unit} onValueChange={(v) => setProduct(i, "unit", v)}>
                          <SelectTrigger data-testid={`review-product-unit-${i}`}><SelectValue placeholder="Choose a unit" /></SelectTrigger>
                          <SelectContent>{UNITS.map((u) => <SelectItem key={u} value={u}>{u}</SelectItem>)}{p.unit && !UNITS.includes(p.unit) && <SelectItem value={p.unit}>{p.unit}</SelectItem>}</SelectContent>
                        </Select>
                      </div>
                    </div>
                    <div className="space-y-1.5"><Label>Description</Label><Textarea rows={2} value={p.description} onChange={(e) => setProduct(i, "description", e.target.value)} /></div>
                    {requiresSource && (
                      <div className="space-y-1.5"><Label>Sourced from</Label><Input value={p.sourced_from} onChange={(e) => setProduct(i, "sourced_from", e.target.value)} placeholder="e.g. Home Farm, Swimbridge" data-testid={`review-product-sourced-${i}`} /></div>
                    )}
                    {p.production_tags?.length > 0 && (
                      <div className="flex flex-wrap gap-1.5">
                        {p.production_tags.map((t) => (
                          <span key={t} className="inline-flex items-center gap-1 rounded-full bg-[#EEF4E6] text-[#3E5A1F] border border-[#cfe0bc] px-2 py-0.5 text-[11px]"><Leaf className="h-3 w-3" />{t}</span>
                        ))}
                      </div>
                    )}
                  </div>
                </div>
              </Card>
            ))}
          </div>

          {/* Account (anonymous) */}
          {!user && (
            <Card className="rounded-2xl p-6 my-5 space-y-4 border-primary/30">
              <h2 className="font-semibold">Create your account</h2>
              <p className="text-sm text-muted-foreground -mt-2">So we can save your listing and let you know when it's live.</p>
              <div className="grid sm:grid-cols-2 gap-4">
                <div className="space-y-1.5"><Label>Your name</Label><Input value={account.full_name} onChange={(e) => setAccount((a) => ({ ...a, full_name: e.target.value }))} data-testid="review-account-name" /></div>
                <div className="space-y-1.5"><Label>Email</Label><Input type="email" value={account.email} onChange={(e) => setAccount((a) => ({ ...a, email: e.target.value }))} data-testid="review-account-email" /></div>
                <div className="space-y-1.5"><Label>Password</Label><Input type="password" value={account.password} onChange={(e) => setAccount((a) => ({ ...a, password: e.target.value }))} data-testid="review-account-password" /></div>
              </div>
            </Card>
          )}

          <Separator className="my-6" />
          <div className="flex flex-col sm:flex-row gap-3">
            <Button variant="secondary" className="flex-1" onClick={() => setView("chat")}>Keep chatting</Button>
            <Button className="flex-1 bg-[color:var(--fg-wood-600)] hover:bg-[color:var(--fg-wood-700)] text-white" onClick={submit} disabled={submitting} data-testid="review-submit-button">
              {submitting ? <Loader2 className="h-4 w-4 mr-1 animate-spin" /> : <ClipboardCheck className="h-4 w-4 mr-1" />} Publish my listing
            </Button>
          </div>
        </div>
      </div>
    );
  }

  // ---------- CHAT ----------
  return (
    <div className="pt-16 min-h-screen bg-grain flex flex-col">
      <div className="max-w-2xl w-full mx-auto px-4 sm:px-6 py-5 flex-1 flex flex-col">
        <div className="flex items-center gap-2 mb-1">
          <span className="h-9 w-9 rounded-xl bg-secondary grid place-items-center text-primary"><Sparkles className="h-5 w-5" /></span>
          <div>
            <h1 className="text-xl font-semibold leading-tight" data-testid="onboarding-title">Let's set up your listing</h1>
            <p className="text-xs text-muted-foreground">Just chat naturally — I'll build your listing as we go.</p>
          </div>
        </div>

        <Card className="rounded-2xl flex-1 flex flex-col overflow-hidden mt-3 mb-3" style={{ minHeight: "52vh" }}>
          <div ref={scrollRef} className="flex-1 overflow-y-auto p-4 space-y-3" data-testid="onboarding-messages">
            {starting && (
              <div className="flex items-center gap-2 text-sm text-muted-foreground"><Loader2 className="h-4 w-4 animate-spin" /> Starting the assistant…</div>
            )}
            {messages.map((m, i) => (
              <div key={i} className={`flex ${m.role === "user" ? "justify-end" : "justify-start"}`}>
                <div
                  data-testid={m.role === "user" ? "chat-user-msg" : "chat-assistant-msg"}
                  className={`max-w-[85%] rounded-2xl px-3.5 py-2.5 text-sm whitespace-pre-wrap leading-relaxed ${
                    m.role === "user"
                      ? "bg-primary text-primary-foreground rounded-br-sm"
                      : "bg-secondary text-foreground rounded-bl-sm"
                  }`}
                >
                  {renderText(m.content)}
                </div>
              </div>
            ))}
            {sending && (
              <div className="flex justify-start">
                <div className="bg-secondary rounded-2xl rounded-bl-sm px-3.5 py-2.5 text-sm text-muted-foreground flex items-center gap-1">
                  <span className="h-1.5 w-1.5 bg-current rounded-full animate-bounce" style={{ animationDelay: "0ms" }} />
                  <span className="h-1.5 w-1.5 bg-current rounded-full animate-bounce" style={{ animationDelay: "120ms" }} />
                  <span className="h-1.5 w-1.5 bg-current rounded-full animate-bounce" style={{ animationDelay: "240ms" }} />
                </div>
              </div>
            )}
          </div>

          <div className="border-t border-border p-3">
            <div className="flex items-end gap-2">
              <Textarea
                value={input}
                onChange={(e) => setInput(e.target.value)}
                onKeyDown={(e) => { if (e.key === "Enter" && !e.shiftKey) { e.preventDefault(); send(); } }}
                placeholder="Type your reply…"
                rows={1}
                className="resize-none min-h-[44px] max-h-32"
                data-testid="onboarding-input"
                disabled={starting}
              />
              <Button onClick={send} disabled={sending || starting || !input.trim()} className="shrink-0 h-11" data-testid="onboarding-send-button">
                {sending ? <Loader2 className="h-4 w-4 animate-spin" /> : <Send className="h-4 w-4" />}
              </Button>
            </div>
          </div>
        </Card>

        <Button
          variant="outline"
          className="w-full border-primary/40 text-primary hover:bg-secondary"
          onClick={buildListing}
          disabled={extracting || starting || messages.length < 3}
          data-testid="onboarding-review-button"
        >
          {extracting ? <Loader2 className="h-4 w-4 mr-1 animate-spin" /> : <ClipboardCheck className="h-4 w-4 mr-1" />}
          Review my listing
        </Button>
        <p className="text-[11px] text-muted-foreground text-center mt-2">
          You'll be able to add photos and check everything before it's submitted.
        </p>
      </div>
    </div>
  );
}
